import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { ArrowUp } from './Icons';
import { useTranslation } from '../contexts/TranslationContext';
import { LandingQaContent } from './LandingQaContent';
import { preloadLoginRoute } from '../utils/loginRoutePreloader';
import { LandingFooter } from './LandingFooter';
import { LandingCallToAction } from './LandingCallToAction';
import { LandingPresentationHero } from './LandingPresentationHero';
import { GradientBackground } from './GradientBackground';
import { LandingHeroRoadBackground } from './LandingHeroRoadBackground';
import { LandingQuestionExamples } from './LandingQuestionExamples';
import type { SharedAnswerExample } from '../services/share';
import { LandingHeader } from './LandingHeader';
import { LandingPricingSection } from './billing/LandingPricingSection';
import { LandingManifestoSection } from './LandingManifestoSection';
import { LandingDemoSection } from './LandingDemoSection';
import { LandingPressSection } from './LandingPressSection';
import { LandingCoverageMap } from './LandingCoverageMap';
import { LandingImpactSection } from './LandingImpactSection';
import type { BillingSubscriptionPriceKey } from '../types/billing';
import type { LandingBillingCatalog } from '../utils/landingBillingCatalog';
import { useLandingNavigationVisibility } from '../hooks/useLandingNavigationVisibility';
import { useLandingSectionNavigation } from '../hooks/useLandingSectionNavigation';
import { LandingTimeSavingsSimulator } from './LandingTimeSavingsSimulator';
import { rememberPublicLandingSection } from '../utils/publicRoutes';
import { useLandingHashScrollRestoration } from '../hooks/useLandingHashScrollRestoration';
import { CookiePreferencesButton } from './CookiePreferencesButton';
import { LandingAgentsSection } from './LandingAgentsSection';
import { AdrApproachComparison } from './AdrApproachComparison';
import type { PlatformImpact } from '../services/platformImpact';

const ContactModal = lazy(() => import('./ContactModal').then((module) => ({
  default: module.ContactModal,
})));

interface LandingPageProps {
  onGetStarted: (priceKey?: BillingSubscriptionPriceKey) => void;
  onSignIn: () => void;
  initiallyVisible?: boolean;
  initialQuestionExamples?: SharedAnswerExample[];
  initialBillingCatalog?: LandingBillingCatalog;
  initialPlatformImpact?: PlatformImpact;
}
const ANIMATED_SECTION_IDS = [
  'hero-content',
  'features-carousel',
  'how-header',
  'how-examples',
  'step-0',
  'step-1',
  'step-2',
  'step-3',
  'coverage-content',
  'manifesto-content',
  'press-content',
  'pricing-content',
  'faq-header',
  'faq-list',
  'cta-content',
];

const PUBLIC_NAV_LINKS = [
  { id: 'features', labelKey: 'nav.features' },
  { id: 'how-it-works', labelKey: 'nav.howItWorks' },
  { id: 'manifesto', labelKey: 'nav.manifesto' },
  { id: 'pricing', labelKey: 'nav.pricing' },
  { id: 'faq', labelKey: 'nav.faq' },
];
function createInitialVisibleSections(shouldShowSections: boolean) {
  return shouldShowSections ? new Set(ANIMATED_SECTION_IDS) : new Set<string>();
}

function visibleAnimatedSectionIds() {
  return Array.from(document.querySelectorAll<HTMLElement>('[data-animate]'))
    .filter((section) => {
      const sectionBounds = section.getBoundingClientRect();
      return sectionBounds.top < window.innerHeight && sectionBounds.bottom > 0;
    })
    .map((section) => section.id);
}

function observeAnimatedSections(observer: IntersectionObserver | null) {
  document.querySelectorAll('[data-animate]').forEach((el) => {
    observer?.observe(el);
  });
}

export function LandingPage({
  onGetStarted,
  onSignIn,
  initiallyVisible = false,
  initialQuestionExamples = [],
  initialBillingCatalog,
  initialPlatformImpact,
}: LandingPageProps) {
  const { t } = useTranslation();
  const [visibleSections, setVisibleSections] = useState<Set<string>>(() => createInitialVisibleSections(initiallyVisible));
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [shouldShowBackToTop, setShouldShowBackToTop] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hasQuestionExamples, setHasQuestionExamples] = useState(initialQuestionExamples.length > 0);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const { shouldShowSectionNavigation } = useLandingNavigationVisibility();
  const navigationLinks = PUBLIC_NAV_LINKS.filter((link) => {
    if (link.id === 'how-it-works') return hasQuestionExamples;
    return true;
  });
  const {
    scrollToSection,
    scrollToSectionFromLink,
    scrollToSectionFromMobileMenu,
  } = useLandingSectionNavigation({
    setIsMobileMenuOpen,
    setVisibleSections,
    visibleSections,
  });
  useLandingHashScrollRestoration(scrollToSection);

  const handleQuestionExamplesAvailability = useCallback((hasExamples: boolean) => {
    setHasQuestionExamples(hasExamples);

    if (hasExamples) {
      observeAnimatedSections(observerRef.current);
      setVisibleSections((currentSections) => {
        const nextSections = new Set(currentSections);
        visibleAnimatedSectionIds()
          .forEach((sectionId) => nextSections.add(sectionId));
        return nextSections;
      });
    }
  }, []);

  useEffect(() => {
    if (initiallyVisible) {
      setVisibleSections(new Set(visibleAnimatedSectionIds()));
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.1 }
    );

    observeAnimatedSections(observerRef.current);

    return () => observerRef.current?.disconnect();
  }, [initiallyVisible]);

  useEffect(() => {
    const hero = document.getElementById('hero');
    if (!hero) {
      return undefined;
    }

    const heroVisibilityObserver = new IntersectionObserver(
      ([entry]) => setShouldShowBackToTop(!entry.isIntersecting),
      { threshold: 0 },
    );

    heroVisibilityObserver.observe(hero);

    return () => heroVisibilityObserver.disconnect();
  }, []);

  const preloadLoginOnIntent = () => {
    void preloadLoginRoute();
  };

  return (
    <div className="theme-public-shell relative min-h-[100svh] overflow-x-hidden overscroll-y-auto touch-pan-y bg-slate-950 text-slate-100">
      <GradientBackground showGrid={false} />

      <LandingHeader
        isDesktopNavigationVisible={shouldShowSectionNavigation}
        isMobileMenuOpen={isMobileMenuOpen}
        navigationLinks={navigationLinks}
        onToggleMobileMenu={() => setIsMobileMenuOpen((isOpen) => !isOpen)}
        onNavigationClick={scrollToSectionFromLink}
        onMobileNavigationClick={scrollToSectionFromMobileMenu}
        onGetStarted={() => onGetStarted()}
        onSignIn={onSignIn}
        onLoginIntent={preloadLoginOnIntent}
      />

      <main>
        <section
          id="hero"
          className="relative flex min-h-[100svh] items-start justify-center px-3 pb-10 pt-20 sm:px-6 sm:pb-12 sm:pt-24"
        >
          <LandingHeroRoadBackground />
          <div className="relative z-10 mx-auto w-full max-w-7xl text-center">
            <div
              id="hero-content"
              data-landing-scroll-target
              data-animate
              className={`transition-all duration-1000 ${
                visibleSections.has('hero-content') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              <LandingPresentationHero
                navigationLinks={navigationLinks}
                onNavigationClick={(event, id) => scrollToSectionFromLink(event, id)}
                onGoToLogin={() => onGetStarted()}
                onSignIn={onSignIn}
                initialPlatformImpact={initialPlatformImpact}
              />
            </div>
          </div>
        </section>

        <LandingAgentsSection
          isVisible={visibleSections.has('features-carousel')}
        />

        <AdrApproachComparison />

        <LandingQuestionExamples
          initialExamples={initialQuestionExamples}
          visibleSections={visibleSections}
          onAvailabilityChange={handleQuestionExamplesAvailability}
        />

        <LandingCoverageMap
          isVisible={visibleSections.has('coverage-content')}
          onGetStarted={() => {
            rememberPublicLandingSection('coverage');
            onGetStarted();
          }}
          onStartIntent={preloadLoginOnIntent}
        />

        <LandingImpactSection initialImpact={initialPlatformImpact} />

        <LandingTimeSavingsSimulator
          onGetStarted={onGetStarted}
          onStartIntent={preloadLoginOnIntent}
        />

        <LandingDemoSection />
        <LandingManifestoSection isVisible={visibleSections.has('manifesto-content')} />
        <LandingPressSection isVisible={visibleSections.has('press-content')} />
        <LandingPricingSection
          enabled
          initialCatalog={initialBillingCatalog}
          initialPlatformImpact={initialPlatformImpact}
          isVisible={visibleSections.has('pricing-content')}
          onSelectPlan={onGetStarted}
          onStartFree={() => onGetStarted()}
          onPlanIntent={preloadLoginOnIntent}
        />
        <LandingQaContent
          visibleSections={visibleSections}
        />
        <LandingCallToAction
          isVisible={visibleSections.has('cta-content')}
          onGetStarted={() => onGetStarted()}
          onStartIntent={preloadLoginOnIntent}
        />

      </main>
      {shouldShowBackToTop && (
        <button
          type="button"
          onClick={() => scrollToSection('hero')}
          className="brand-floating-button fixed bottom-5 left-1/2 z-40 inline-flex h-11 w-11 -translate-x-1/2 items-center justify-center rounded-full border text-sm font-semibold backdrop-blur-xl transition-all duration-300 sm:bottom-8"
          aria-label={t('nav.backToTop')}
          title={t('nav.backToTop')}
        >
          <ArrowUp className="h-5 w-5" />
        </button>
      )}

      <CookiePreferencesButton />

      <LandingFooter
        onContactClick={() => setIsContactOpen(true)}
        onGetStarted={() => onGetStarted()}
      />
      {isContactOpen && (
        <Suspense fallback={null}>
          <ContactModal isOpen onClose={() => setIsContactOpen(false)} />
        </Suspense>
      )}
    </div>
  );
}
